import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { CreditAllocationModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/creditAllocation/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/creditAllocation/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/creditAllocation/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/creditAllocation/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/creditAllocation/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/creditAllocation/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/creditAllocation/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/creditAllocation/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/creditAllocation/UPDATE_FAILURE'

export type CreditAllocationModuleState = {
  creating: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CreditAllocationModuleState = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCreditAllocations(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().creditAllocations.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/creditAllocations', options)
      .then(({ data }: { data: CreditAllocationModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CREDIT_ALLOCATION_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCreditAllocation(creditAllocationId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.creditAllocations.loadedIds]
    const loadedForKeys = [...state.creditAllocations.loadedForKeys]

    if (creditAllocationId && !loadedIds.includes(creditAllocationId)){
      loadedIds.push(creditAllocationId)
    }

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/creditAllocations/${creditAllocationId}`, options)
      .then(({ data }: { data: CreditAllocationModel }) => {
        const normalizedJson = normalize(data, Schemas.CREDIT_ALLOCATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCreditAllocation(creditAllocation: Partial<CreditAllocationModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify(creditAllocation),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/creditAllocations', options, config)
      .then(({ data }: { data: CreditAllocationModel }) => {
        const normalizedJson = normalize(data, Schemas.CREDIT_ALLOCATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCreditAllocation(
  creditAllocation: UpdateParams<CreditAllocationModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(creditAllocation),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/creditAllocations/${creditAllocation.id}`, options, config)
      .then(({ data }: { data: CreditAllocationModel }) => {
        const normalizedJson = normalize(data, Schemas.CREDIT_ALLOCATION)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CreditAllocationModuleState = initialState,
  action: Action = { type: '' },
): CreditAllocationModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
